/** A very small IndexedDB wrapper.
 *
 *  IndexedDB rather than localStorage: the queue needs transactions so a replay
 *  cannot half-happen, and localStorage is neither transactional nor large
 *  enough for cached days. No dependency — the surface used here is three
 *  operations wide.
 *
 *  Every store is opened per destination (see `state/destinations.ts`), so two
 *  destinations cannot see each other's cache or queue. Retrofitting that later
 *  would mean an on-device migration, which cannot be run server-side.
 */

const DB_NAME = 'bujo';
const DB_VERSION = 1;

export const CACHE_STORE = 'cache';
export const QUEUE_STORE = 'queue';

let opening: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  if (opening) return opening;
  opening = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(CACHE_STORE)) db.createObjectStore(CACHE_STORE);
      if (!db.objectStoreNames.contains(QUEUE_STORE)) {
        db.createObjectStore(QUEUE_STORE, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  return opening;
}

function run<T>(
  store: string,
  mode: IDBTransactionMode,
  work: (s: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(store, mode);
        const request = work(tx.objectStore(store));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      }),
  );
}

export const idb = {
  get: <T>(store: string, key: string) => run<T | undefined>(store, 'readonly', (s) => s.get(key)),
  put: (store: string, value: unknown, key?: string) =>
    run(store, 'readwrite', (s) => (key === undefined ? s.put(value) : s.put(value, key))),
  del: (store: string, key: string) => run(store, 'readwrite', (s) => s.delete(key)),
  all: <T>(store: string) => run<T[]>(store, 'readonly', (s) => s.getAll()),
};

/** Whether persistent storage is even available. Safari in private browsing
 *  refuses IndexedDB, and failing loudly there beats losing a capture. */
export async function storageAvailable(): Promise<boolean> {
  try {
    await open();
    return true;
  } catch {
    return false;
  }
}
